import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { customer_account_base_url } from "../../../../utils/urls";

const POWER_BASE =
  "/facility/utility_management/meter_management/power_meters/view_power_meter_tabs";
const WATER_BASE =
  "/facility/utility_management/meter_management/water_meters";

const normalizeType = (type) =>
  String(type || "").toLowerCase() === "postpaid" ? "Postpaid" : "Prepaid";

// Resolves the account view route for a power/water account.
const buildTarget = (utility, type, id) => {
  if (utility === "power") {
    const page =
      type === "Postpaid"
        ? "view_postpaid_power_customer"
        : "view_prepaid_power_customer";
    return { path: `${POWER_BASE}/${page}/${id}`, state: undefined };
  }
  const path =
    type === "Postpaid"
      ? `${WATER_BASE}/view_customer_account`
      : `${WATER_BASE}/view_meter_tabs/customer_account/view_customer_details1/${id}`;
  return { path, state: { accountId: id } };
};

// Account maps are cached per facility and shared by every table that uses the
// hook, so links don't disappear while a tab remounts or a request is retried.
const CACHE_TTL_MS = 5 * 60 * 1000;
const MAX_ATTEMPTS = 3;
const cache = new Map(); // facilityId -> { map, loadedAt, promise }
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn());
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchList = async (url, method, body) => {
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const res = await makeRequest2(url, method, body);
      if (res?.success) {
        const list = res.data?.data;
        return Array.isArray(list) ? list : [];
      }
    } catch (_) {
      // retry below
    }
    if (attempt < MAX_ATTEMPTS) await wait(500 * attempt);
  }
  return null; // every attempt failed
};

const loadAccounts = (facilityId) => {
  const entry = cache.get(facilityId) || { map: null, loadedAt: 0, promise: null };
  cache.set(facilityId, entry);
  if (entry.promise) return entry.promise;
  if (entry.map && Date.now() - entry.loadedAt < CACHE_TTL_MS) return Promise.resolve(entry.map);

  entry.promise = (async () => {
    const [power, water, levy] = await Promise.all([
      fetchList(
        `/api/app/power_meter_management/controllers/customer_management/get_facility_customer_accounts/${facilityId}`,
        "GET",
      ),
      fetchList(`${customer_account_base_url}/all/${facilityId}`, "GET", null),
      fetchList(`/api/app/payment_management/get_levy_accounts/${facilityId}`, "GET"),
    ]);
    const lists = { power, water, levy };

    // Keep the previous map when everything failed, and for a list that failed
    // keep what we already knew about it.
    const previous = entry.map || {};
    const map = {};
    const add = (list, utility) => {
      (list || []).forEach((acc) => {
        if (!acc?.account_no) return;
        // Power accounts carry unitInfo.unitName; water and levy accounts carry
        // unitName (water: "N/A" when the account has no unit).
        const unitName = acc.unitInfo?.unitName || acc.unitName;
        const unitId = acc.unitInfo?._id || acc.unitId;
        map[String(acc.account_no)] = {
          id: acc._id || acc.id,
          utility,
          // Prepaid/Postpaid applies to power and water only
          type: utility === "levy" ? null : normalizeType(acc.payment_type),
          unitName: unitName && unitName !== "N/A" ? unitName : null,
          unitId: unitId ? String(unitId) : null,
        };
      });
    };
    add(levy, "levy");
    add(water, "water");
    add(power, "power");
    Object.entries(previous).forEach(([key, acc]) => {
      const failed = lists[acc.utility] === null;
      if (failed && !map[key]) map[key] = acc;
    });

    if (Object.values(lists).some((list) => list !== null)) {
      entry.map = map;
      entry.loadedAt = Date.now();
    }
    entry.promise = null;
    notify();
    return entry.map || {};
  })();
  return entry.promise;
};

/**
 * Exposes the facility's power, water and levy accounts by account number
 * (account_no) plus a navigate helper.
 */
const useAccountLookup = () => {
  const navigate = useNavigate();
  const [version, setVersion] = useState(0);
  const facilityRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const rerender = () => !cancelled && setVersion((v) => v + 1);
    listeners.add(rerender);

    (async () => {
      const facilityId = await getItem("selectedFacilityId");
      if (!facilityId || cancelled) return;
      facilityRef.current = facilityId;
      rerender(); // pick up an already-cached map straight away
      await loadAccounts(facilityId);
    })();

    return () => {
      cancelled = true;
      listeners.delete(rerender);
    };
  }, []);

  const getAccount = useCallback(
    (accountNumber) => {
      const map = cache.get(facilityRef.current)?.map;
      return map?.[String(accountNumber || "")] || null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [version], // new identity when accounts load, so tables re-render their links
  );

  const openAccount = useCallback(
    async (accountNumber) => {
      const facilityId = facilityRef.current || (await getItem("selectedFacilityId"));
      const map = (await loadAccounts(facilityId)) || {};
      const acc = map[String(accountNumber || "")];
      if (!acc || !acc.type) return; // only power/water accounts have a page
      const { path, state } = buildTarget(acc.utility, acc.type, acc.id);
      navigate(path, state ? { state } : undefined);
    },
    [navigate],
  );

  // Account numbers of every "water" or "power" account in the facility.
  // Waits for the accounts to load.
  const getAccountNumbers = useCallback(async (utility) => {
    const facilityId = facilityRef.current || (await getItem("selectedFacilityId"));
    const map = (await loadAccounts(facilityId)) || {};
    return Object.keys(map).filter((no) => map[no].utility === utility);
  }, []);

  return { getAccount, openAccount, getAccountNumbers };
};

export default useAccountLookup;
